import { useState } from "react";
import { FileUp, FileSpreadsheet, FileText, Globe, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ALL_EXPORT_COLUMNS } from "@/server/export/csv-writer";
import { downloadBlob } from "@/lib/download-blob";

interface PublicExportButtonProps {
  token: string;
}

export function PublicExportButton({ token }: PublicExportButtonProps) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<"csv" | "xlsx" | "kml">("csv");
  const [selectedColumns, setSelectedColumns] = useState<Set<string> | null>(null);

  const csvMutation = trpc.share.exportCsv.useMutation();
  const xlsxMutation = trpc.share.exportXlsx.useMutation();
  const kmlMutation = trpc.share.exportKml.useMutation();
  const isExporting = csvMutation.isPending || xlsxMutation.isPending || kmlMutation.isPending;

  const defaultKeys = new Set([
    "name", "primary_type", "address", "lat", "lng", "rating",
    "ownership", "has_sport_field", "confidence", "source",
  ]);
  const cols = selectedColumns ?? defaultKeys;

  function toggleColumn(key: string) {
    const next = new Set(cols);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedColumns(next);
  }

  async function handleExport() {
    if (format === "csv") {
      const result = await csvMutation.mutateAsync({ token, columns: [...cols] });
      downloadBlob(result.data, result.filename, result.mimeType);
    } else if (format === "xlsx") {
      const result = await xlsxMutation.mutateAsync({ token, columns: [...cols] });
      downloadBlob(result.data, result.filename, result.mimeType, true);
    } else {
      const result = await kmlMutation.mutateAsync({ token });
      downloadBlob(result.data, result.filename, result.mimeType);
    }
    setOpen(false);
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <FileUp className="h-4 w-4 mr-1.5" />
        Export
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Export Locations</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Column checkboxes (hidden for KML) */}
            {format !== "kml" && (
              <div className="space-y-1.5">
                <Label>Columns</Label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 max-h-48 overflow-y-auto rounded border p-3">
                  {ALL_EXPORT_COLUMNS.map((col) => (
                    <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={cols.has(col.key)}
                        onCheckedChange={() => toggleColumn(col.key)}
                      />
                      {col.header}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Format */}
            <div className="space-y-1.5">
              <Label>Format</Label>
              <div className="flex gap-3">
                <Button
                  variant={format === "csv" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormat("csv")}
                >
                  <FileText className="h-4 w-4 mr-1.5" />
                  CSV
                </Button>
                <Button
                  variant={format === "xlsx" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormat("xlsx")}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                  XLSX
                </Button>
                <Button
                  variant={format === "kml" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormat("kml")}
                >
                  <Globe className="h-4 w-4 mr-1.5" />
                  KML
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={isExporting || (format !== "kml" && cols.size === 0)}>
              {isExporting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Export {format.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
